import React, {
    useEffect,
    useState
} from "react";

import {
    useNavigate,
    useSearchParams
} from "react-router-dom";

import {
    ArrowLeft,
    Save,
    Plus,
    Trash2,
    RefreshCw,
    Edit3,
    Mic
} from "lucide-react";

import {
    getChambers,
    createGRN,
    getGRNById,
    updateGRN
} from "../services/api";

import "./GoodsReceived.css";


/* =========================================================
   VOICE INPUT COMPONENT
========================================================= */

const VoiceInput = ({
    value,
    onChange,
    placeholder,
    type = "text",
    rows,
    listening,
    onVoice
}) => {

    const isTextarea =
        rows !== undefined;

    return (
        <div className="grn-voice-input-wrapper">

            {isTextarea ? (

                <textarea
                    value={value}
                    onChange={onChange}
                    placeholder={placeholder}
                    rows={rows}
                />

            ) : (

                <input
                    type={
                        type === "number"
                            ? "text"
                            : type
                    }
                    inputMode={
                        type === "number"
                            ? "decimal"
                            : undefined
                    }
                    value={value}
                    onChange={onChange}
                    placeholder={placeholder}
                />

            )}

            <button
                type="button"
                className={
                    listening
                        ? "grn-mic-button grn-mic-listening"
                        : "grn-mic-button"
                }
                onClick={onVoice}
                title={
                    listening
                        ? "Listening..."
                        : "Voice input"
                }
                aria-label="Voice input"
            >
                <Mic
                    size={25}
                    strokeWidth={2.2}
                />
            </button>

        </div>
    );
};


/* =========================================================
   GOODS RECEIVED
========================================================= */

const GoodsReceived = () => {

    const navigate =
        useNavigate();

    const [searchParams] =
        useSearchParams();


    /* =====================================================
       EDIT MODE
    ===================================================== */

    const editId =
        searchParams.get("edit");

    const isEditMode =
        Boolean(editId);


    /* =====================================================
       MAIN FORM
    ===================================================== */

    const [form, setForm] =
        useState({

            grnDate:
                new Date()
                    .toISOString()
                    .split("T")[0],

            customerName: "",

            vehicleNo: "",

            driverName: "",

            partyNo: "",

            fullLorryWeightKg: "",

            emptyLorryWeightKg: "",

            remarks: ""

        });


    /* =====================================================
       ITEMS
    ===================================================== */

    const [items, setItems] =
        useState([

            {
                chamberId: "",
                lotNo: "",
                productMark: "",
                bags: "",
                rottenBags: "0",
                weightKg: "",
                floorNo: "",
                location: ""
            }

        ]);


    /* =====================================================
       CHAMBERS
    ===================================================== */

    const [chambers, setChambers] =
        useState([]);


    /* =====================================================
       LOADING
    ===================================================== */

    const [loading, setLoading] =
        useState(false);

    const [loadingGRN, setLoadingGRN] =
        useState(false);

    const [loadingChambers, setLoadingChambers] =
        useState(true);


    /* =====================================================
       VOICE
    ===================================================== */

    const [listeningField, setListeningField] =
        useState("");


    /* =====================================================
       MESSAGES
    ===================================================== */

    const [error, setError] =
        useState("");

    const [success, setSuccess] =
        useState("");


    /* =====================================================
       LOAD CHAMBERS
    ===================================================== */

    const loadChambers =
        async () => {

            try {

                setLoadingChambers(true);

                setError("");

                const response =
                    await getChambers();

                console.log(
                    "Chambers API Response:",
                    response
                );

                const responseData =
                    response?.data;

                let chamberData = [];

                if (
                    Array.isArray(
                        responseData?.data
                    )
                ) {

                    chamberData =
                        responseData.data;

                } else if (
                    Array.isArray(
                        responseData
                    )
                ) {

                    chamberData =
                        responseData;

                }

                console.log(
                    "Chambers Loaded:",
                    chamberData
                );

                setChambers(
                    chamberData
                );

                if (
                    chamberData.length === 0
                ) {

                    setError(
                        "No chambers found. Please check the Chambers table."
                    );

                }

            } catch (err) {

                console.error(
                    "Chambers Error:",
                    err
                );

                setChambers([]);

                setError(
                    err?.response?.data?.message ||
                    err?.message ||
                    "Unable to load chambers."
                );

            } finally {

                setLoadingChambers(false);

            }

        };


    /* =====================================================
       INITIAL CHAMBERS
    ===================================================== */

    useEffect(() => {

        loadChambers();

    }, []);


    /* =====================================================
       LOAD EXISTING GRN
    ===================================================== */

    useEffect(() => {

        if (editId) {

            loadExistingGRN(
                editId
            );

        }

    }, [editId]);


    /* =====================================================
       LOAD EXISTING GRN
    ===================================================== */

    const loadExistingGRN =
        async (id) => {

            try {

                setLoadingGRN(true);

                setError("");

                setSuccess("");

                const response =
                    await getGRNById(id);

                console.log(
                    "Existing GRN Response:",
                    response
                );

                const grn =
                    response?.data?.data ||
                    response?.data;

                if (!grn) {

                    throw new Error(
                        "GRN data not found."
                    );

                }


                /* DATE */

                let grnDate =
                    grn.grn_date ||
                    grn.grnDate;

                if (grnDate) {

                    grnDate =
                        String(
                            grnDate
                        ).substring(
                            0,
                            10
                        );

                }


                /* MAIN FORM */

                setForm({

                    grnDate:
                        grnDate || "",

                    customerName:
                        grn.customer_name ??
                        grn.customerName ??
                        "",

                    vehicleNo:
                        grn.vehicle_no ??
                        grn.vehicleNo ??
                        "",

                    driverName:
                        grn.driver_name ??
                        grn.driverName ??
                        "",

                    partyNo:
                        grn.party_no ??
                        grn.partyNo ??
                        "",

                    fullLorryWeightKg:
                        grn.full_lorry_weight_kg ??
                        grn.fullLorryWeightKg ??
                        "",

                    emptyLorryWeightKg:
                        grn.empty_lorry_weight_kg ??
                        grn.emptyLorryWeightKg ??
                        "",

                    remarks:
                        grn.remarks ??
                        ""

                });


                /* ITEMS */

                const savedItems =
                    Array.isArray(
                        grn.items
                    )
                        ? grn.items
                        : [];

                if (
                    savedItems.length > 0
                ) {

                    setItems(

                        savedItems.map(
                            (item) => ({

                                chamberId:
                                    item.chamber_id ??
                                    item.chamberId ??
                                    "",

                                lotNo:
                                    item.lot_no ??
                                    item.lotNo ??
                                    "",

                                productMark:
                                    item.product_mark ??
                                    item.productMark ??
                                    "",

                                bags:
                                    item.bags_quantity ??
                                    item.bagsQuantity ??
                                    item.bags ??
                                    "",

                                rottenBags:
                                    item.rotten_bags ?? item.rottenBags ?? 0,

                                weightKg:
                                    item.weight_kg ??
                                    item.weightKg ??
                                    "",

                                floorNo: item.floor_no ?? item.floorNo ?? "",

                                location:
                                    item.location ??
                                    ""

                            })
                        )

                    );

                } else {

                    setItems([

                        {
                            chamberId: "",
                            lotNo: "",
                            productMark: "",
                            bags: "",
                            rottenBags: "0",
                            weightKg: "",
                            floorNo: "",
                            location: ""
                        }

                    ]);

                }

            } catch (err) {

                console.error(
                    "Load GRN Error:",
                    err
                );

                setError(
                    err?.response?.data?.message ||
                    err?.message ||
                    "Unable to load GRN."
                );

            } finally {

                setLoadingGRN(false);

            }

        };


    /* =====================================================
       FORM CHANGE
    ===================================================== */

    const handleChange =
        (e) => {

            const {
                name,
                value
            } = e.target;

            setForm(
                previous => ({

                    ...previous,

                    [name]: value

                })
            );

            setError("");

            setSuccess("");

        };


    /* =====================================================
       ITEM CHANGE
    ===================================================== */

    const handleItemChange =
        (
            index,
            field,
            value
        ) => {

            setItems(
                previous => {

                    const updated =
                        [...previous];

                    updated[index] = {

                        ...updated[index],

                        [field]: value

                    };

                    return updated;

                }
            );

            setError("");

            setSuccess("");

        };


    /* =====================================================
       SPOKEN NUMBER CONVERSION
    ===================================================== */

    const convertSpokenNumber =
        (text) => {

            if (!text) {

                return "";

            }

            let cleaned =
                String(text)
                    .toLowerCase()
                    .trim()
                    .replace(
                        /,/g,
                        ""
                    );

            cleaned =
                cleaned
                    .replace(
                        /\b(kilograms?|kgs?|kg|bags?|bag|numbers?|number)\b/g,
                        ""
                    )
                    .trim();


            /* Already numeric */

            if (
                /^[0-9]+(\.[0-9]+)?$/.test(
                    cleaned
                )
            ) {

                return cleaned;

            }


            /* Number words */

            const ones = {

                zero: 0,
                one: 1,
                two: 2,
                three: 3,
                four: 4,
                five: 5,
                six: 6,
                seven: 7,
                eight: 8,
                nine: 9,
                ten: 10,
                eleven: 11,
                twelve: 12,
                thirteen: 13,
                fourteen: 14,
                fifteen: 15,
                sixteen: 16,
                seventeen: 17,
                eighteen: 18,
                nineteen: 19

            };


            const tens = {

                twenty: 20,
                thirty: 30,
                forty: 40,
                fifty: 50,
                sixty: 60,
                seventy: 70,
                eighty: 80,
                ninety: 90

            };


            const words =
                cleaned
                    .replace(
                        /-/g,
                        " "
                    )
                    .split(/\s+/)
                    .filter(Boolean);


            let total = 0;

            let current = 0;

            let foundNumber =
                false;


            for (
                let i = 0;
                i < words.length;
                i++
            ) {

                const word =
                    words[i];


                if (
                    ones[word] !== undefined
                ) {

                    current +=
                        ones[word];

                    foundNumber =
                        true;

                }

                else if (
                    tens[word] !== undefined
                ) {

                    current +=
                        tens[word];

                    foundNumber =
                        true;

                }

                else if (
                    word === "hundred"
                ) {

                    if (
                        current === 0
                    ) {

                        current = 1;

                    }

                    current *= 100;

                    foundNumber =
                        true;

                }

                else if (
                    word === "thousand"
                ) {

                    if (
                        current === 0
                    ) {

                        current = 1;

                    }

                    total +=
                        current *
                        1000;

                    current = 0;

                    foundNumber =
                        true;

                }

                else if (
                    word === "lakh" ||
                    word === "lakhs"
                ) {

                    if (
                        current === 0
                    ) {

                        current = 1;

                    }

                    total +=
                        current *
                        100000;

                    current = 0;

                    foundNumber =
                        true;

                }

                else if (
                    word === "crore" ||
                    word === "crores"
                ) {

                    if (
                        current === 0
                    ) {

                        current = 1;

                    }

                    total +=
                        current *
                        10000000;

                    current = 0;

                    foundNumber =
                        true;

                }

                else if (
                    word === "million"
                ) {

                    if (
                        current === 0
                    ) {

                        current = 1;

                    }

                    total +=
                        current *
                        1000000;

                    current = 0;

                    foundNumber =
                        true;

                }

                else if (
                    word === "and"
                ) {

                    continue;

                }

            }


            if (
                foundNumber
            ) {

                return String(
                    total +
                    current
                );

            }


            return text;

        };


    /* =====================================================
       VOICE INPUT
    ===================================================== */

    const startVoiceInput =
        ({
            fieldName,
            type = "text",
            itemIndex = null,
            itemField = null
        }) => {

            const SpeechRecognition =
                window.SpeechRecognition ||
                window.webkitSpeechRecognition;


            if (!SpeechRecognition) {

                alert(
                    "Voice input is not supported in this browser. Please use Google Chrome or Microsoft Edge."
                );

                return;

            }


            const recognition =
                new SpeechRecognition();


           recognition.lang =
    fieldName === "customerName" || fieldName === "remarks"
        ? "mr-IN"
        : "en-IN";

        
            recognition.continuous =
                false;

            recognition.interimResults =
                false;

            recognition.maxAlternatives =
                3;


            const listeningKey =
                itemIndex !== null
                    ? `item-${itemIndex}-${itemField}`
                    : fieldName;


            setListeningField(
                listeningKey
            );


            console.log(
                "Starting microphone:",
                listeningKey
            );


            recognition.onstart =
                () => {

                    console.log(
                        "🎤 Listening..."
                    );

                };


            recognition.onresult =
                (event) => {

                    let spokenText =
                        event
                            ?.results?.[0]?.[0]
                            ?.transcript
                            ?.trim() ||
                        "";


                    console.log(
                        "🎤 Speech result:",
                        spokenText
                    );


                    if (
                        !spokenText
                    ) {

                        return;

                    }


                    /* NUMBER FIELD */

                    if (
                        type === "number"
                    ) {

                        spokenText =
                            convertSpokenNumber(
                                spokenText
                            );

                        console.log(
                            "Converted number:",
                            spokenText
                        );

                    }


                    /* MAIN FORM */

                    if (
                        itemIndex === null
                    ) {

                        setForm(
                            previous => ({

                                ...previous,

                                [fieldName]:
                                    spokenText

                            })
                        );

                    }


                    /* ITEM */

                    else {

                        setItems(
                            previous => {

                                const updated =
                                    [...previous];

                                updated[
                                    itemIndex
                                ] = {

                                    ...updated[
                                        itemIndex
                                    ],

                                    [itemField]:
                                        spokenText

                                };

                                return updated;

                            }
                        );

                    }


                    setError("");

                    setSuccess("");

                };


            recognition.onerror = (event) => {
                const errorCode = event?.error || "unknown";

                console.error("Speech recognition error:", errorCode);

                // An intentional browser abort should not show an error popup.
                if (errorCode === "aborted") {
                    setListeningField("");
                    return;
                }

                if (
                    errorCode === "not-allowed" ||
                    errorCode === "service-not-allowed"
                ) {
                    alert(
                        "Microphone permission was denied. Please allow microphone access for this website."
                    );
                } else if (errorCode === "no-speech") {
                    alert(
                        "No speech was detected. Please click the microphone and speak clearly."
                    );
                } else if (errorCode === "audio-capture") {
                    alert(
                        "No microphone was detected. Please check your microphone."
                    );
                } else if (errorCode === "network") {
                    alert(
                        "Speech recognition needs a network connection. Please check your internet connection."
                    );
                } else {
                    alert(`Voice recognition failed: ${errorCode}`);
                }

                setListeningField("");
            };


            recognition.onend =
                () => {

                    console.log(
                        "🎤 Voice input stopped."
                    );

                    setListeningField("");

                };


            try {

                recognition.start();

            } catch (err) {

                console.error(
                    "Unable to start voice recognition:",
                    err
                );

                setListeningField("");

            }

        };


    /* =====================================================
       ADD ITEM
    ===================================================== */

    const addItem =
        () => {

            setItems(
                previous => [

                    ...previous,

                    {
                        chamberId: "",
                        lotNo: "",
                        productMark: "",
                        bags: "",
                        rottenBags: "0",
                        weightKg: "",
                        floorNo: "",
                        location: ""
                    }

                ]
            );

        };


    /* =====================================================
       REMOVE ITEM
    ===================================================== */

    const removeItem =
        (index) => {

            if (
                items.length === 1
            ) {

                return;

            }


            setItems(
                previous =>
                    previous.filter(
                        (_, itemIndex) =>
                            itemIndex !==
                            index
                    )
            );

        };


    /* =====================================================
       VALIDATION
    ===================================================== */

    const validateForm =
        () => {

            if (
                !form.grnDate
            ) {

                return (
                    "Please select GRN date."
                );

            }


            if (
                !form.customerName.trim()
            ) {

                return (
                    "Please enter customer name."
                );

            }


            if (
                items.length === 0
            ) {

                return (
                    "Please add at least one item."
                );

            }


            for (
                let index = 0;
                index < items.length;
                index++
            ) {

                const item =
                    items[index];


                if (
                    !item.chamberId
                ) {

                    return (
                        `Please select chamber for item ${
                            index + 1
                        }.`
                    );

                }


                if (


                    !Number.isInteger(Number(item.floorNo)) ||


                    Number(item.floorNo) < 1 ||


                    Number(item.floorNo) > 6


                ) {


                    return `Please select floor (1–6) for item ${index + 1}.`;


                }



                if (!String(
                        item.lotNo || ""
                    ).trim()
                ) {

                    return (
                        `Please enter lot number for item ${
                            index + 1
                        }.`
                    );

                }


                if (
                    Number(
                        item.bags || 0
                    ) <= 0
                ) {

                    return (
                        `Please enter valid bags quantity for item ${
                            index + 1
                        }.`
                    );

                }


                const rottenBags = Number(item.rottenBags || 0);
                const totalBags = Number(item.bags || 0);

                if (
                    !Number.isInteger(rottenBags) ||
                    rottenBags < 0 ||
                    rottenBags > totalBags
                ) {
                    return `Rotten bags for item ${index + 1} must be a whole number between 0 and total bags.`;
                }


                if (
                    Number(
                        item.weightKg || 0
                    ) <= 0
                ) {

                    return (
                        `Please enter valid weight for item ${
                            index + 1
                        }.`
                    );

                }

            }


            return "";

        };


    /* =====================================================
       SAVE / UPDATE GRN
    ===================================================== */

    const handleSubmit =
        async (e) => {

            e.preventDefault();

            setError("");

            setSuccess("");


            const validationError =
                validateForm();


            if (
                validationError
            ) {

                setError(
                    validationError
                );

                return;

            }


            try {

                setLoading(true);


                /* PREPARE ITEMS */

                const preparedItems =
                    items.map(
                        (item) => ({

                            chamberId:
                                Number(
                                    item.chamberId
                                ),

                            lotNo:
                                String(
                                    item.lotNo ||
                                    ""
                                ).trim(),

                            productMark:
                                String(
                                    item.productMark ||
                                    ""
                                ).trim(),

                            bags:
                                Number(
                                    item.bags ||
                                    0
                                ),

                            rottenBags:
                                Number(item.rottenBags || 0),

                            weightKg:
                                Number(
                                    item.weightKg ||
                                    0
                                ),

                            floorNo: Number(item.floorNo),

                            location:
                                String(
                                    item.location ||
                                    ""
                                ).trim()

                        })
                    );


                /* LORRY WEIGHTS */

                const fullWeight =
                    Number(
                        form.fullLorryWeightKg ||
                        0
                    );


                const emptyWeight =
                    Number(
                        form.emptyLorryWeightKg ||
                        0
                    );


                const netWeight =
                    Math.max(
                        0,
                        fullWeight -
                        emptyWeight
                    );


                /* REQUEST */

                const data = {

                    grnDate:
                        form.grnDate,

                    customerName:
                        form.customerName.trim(),

                    vehicleNo:
                        form.vehicleNo.trim(),

                    driverName:
                        form.driverName.trim(),

                    partyNo:
                        form.partyNo.trim(),

                    fullLorryWeightKg:
                        fullWeight,

                    emptyLorryWeightKg:
                        emptyWeight,

                    netWeightKg:
                        netWeight,

                    remarks:
                        form.remarks.trim(),

                    items:
                        preparedItems

                };


                console.log(
                    "GRN Request Data:",
                    data
                );


                let response;


                /* UPDATE */

                if (
                    isEditMode
                ) {

                    response =
                        await updateGRN(
                            editId,
                            data
                        );

                    setSuccess(
                        "GRN updated successfully."
                    );

                }

                /* CREATE */

                else {

                    response =
                        await createGRN(
                            data
                        );

                    setSuccess(
                        "GRN created successfully."
                    );

                }


                console.log(
                    "GRN Save Response:",
                    response
                );


                /* SAVED ID */

                const savedId =
                    response?.data?.data?.id ??
                    response?.data?.data?.grnId ??
                    response?.data?.id ??
                    editId;


                /* OPEN PRINT PAGE */

                setTimeout(
                    () => {

                        if (
                            savedId
                        ) {

                            navigate(
                                `/goods-received/print/${savedId}`
                            );

                        } else {

                            navigate(
                                "/inventory"
                            );

                        }

                    },
                    700
                );


            } catch (err) {

                console.error(
                    "Save GRN Error:",
                    err
                );

                setError(
                    err?.response?.data?.message ||
                    err?.message ||
                    "Unable to save GRN."
                );

            } finally {

                setLoading(false);

            }

        };


    /* =====================================================
       BACK
    ===================================================== */

    const handleBack =
        () => {

            navigate(
                "/inventory"
            );

        };


    /* =====================================================
       LOADING EXISTING GRN
    ===================================================== */

    if (
        isEditMode &&
        loadingGRN
    ) {

        return (

            <div className="grn-loading-page">

                <RefreshCw
                    size={28}
                    className="grn-loading-spin"
                />

                <h3>
                    Loading GRN...
                </h3>

                <p>
                    Loading the saved receipt information.
                </p>

            </div>

        );

    }


    /* =====================================================
       PAGE
    ===================================================== */

    return (

        <div className="grn-page">


            {/* =================================================
                PAGE HEADER
            ================================================= */}

            <div className="grn-page-header">

                <div>

                    <button
                        type="button"
                        className="grn-back-button"
                        onClick={
                            handleBack
                        }
                    >

                        <ArrowLeft
                            size={17}
                        />

                        Back to Inventory

                    </button>


                    <div className="grn-heading">

                        <div className="grn-heading-icon">

                            {isEditMode ? (

                                <Edit3
                                    size={23}
                                />

                            ) : (

                                <Save
                                    size={23}
                                />

                            )}

                        </div>


                        <div>

                            <h1>

                                {
                                    isEditMode
                                        ? "Edit Goods Received Note"
                                        : "Goods Received Note"
                                }

                            </h1>

                            <p>

                                {
                                    isEditMode
                                        ? "Update the existing receipt information"
                                        : "Record goods received and storage details"
                                }

                            </p>

                        </div>

                    </div>

                </div>

            </div>


            {/* =================================================
                ERROR
            ================================================= */}

            {error && (

                <div className="grn-form-error">

                    {error}

                </div>

            )}


            {/* =================================================
                SUCCESS
            ================================================= */}

            {success && (

                <div className="grn-form-success">

                    {success}

                </div>

            )}


            {/* =================================================
                FORM
            ================================================= */}

            <form
                onSubmit={
                    handleSubmit
                }
            >


                {/* =================================================
                    RECEIPT DETAILS
                ================================================= */}

                <div className="grn-form-card">

                    <div className="grn-form-card-header">

                        <div>

                            <h2>
                                Receipt Details
                            </h2>

                            <p>
                                Basic information about the received goods
                            </p>

                        </div>


                        {isEditMode && (

                            <span className="grn-edit-badge">
                                Editing Saved Receipt
                            </span>

                        )}

                    </div>


                    <div className="grn-form-grid">


                        {/* GRN DATE */}

                        <div className="grn-form-group">

                            <label>
                                GRN Date *
                            </label>

                            <input
                                type="date"
                                name="grnDate"
                                value={
                                    form.grnDate
                                }
                                onChange={
                                    handleChange
                                }
                                required
                            />

                        </div>


                        {/* CUSTOMER */}

                        <div className="grn-form-group">

                            <label>
                                Customer / M/s *
                            </label>

                            <VoiceInput
                                value={
                                    form.customerName
                                }
                                onChange={
                                    (e) =>
                                        handleChange({
                                            target: {
                                                name:
                                                    "customerName",
                                                value:
                                                    e.target.value
                                            }
                                        })
                                }
                                placeholder="Enter customer name"
                                listening={
                                    listeningField ===
                                    "customerName"
                                }
                                onVoice={() =>
                                    startVoiceInput({
                                        fieldName:
                                            "customerName"
                                    })
                                }
                            />

                        </div>


                        {/* VEHICLE */}

                        <div className="grn-form-group">

                            <label>
                                Vehicle No.
                            </label>

                            <VoiceInput
                                value={
                                    form.vehicleNo
                                }
                                onChange={
                                    (e) =>
                                        handleChange({
                                            target: {
                                                name:
                                                    "vehicleNo",
                                                value:
                                                    e.target.value
                                            }
                                        })
                                }
                                placeholder="MH-14-XX-0000"
                                listening={
                                    listeningField ===
                                    "vehicleNo"
                                }
                                onVoice={() =>
                                    startVoiceInput({
                                        fieldName:
                                            "vehicleNo"
                                    })
                                }
                            />

                        </div>


                        {/* DRIVER */}

                        <div className="grn-form-group">

                            <label>
                                Driver Name
                            </label>

                            <VoiceInput
                                value={
                                    form.driverName
                                }
                                onChange={
                                    (e) =>
                                        handleChange({
                                            target: {
                                                name:
                                                    "driverName",
                                                value:
                                                    e.target.value
                                            }
                                        })
                                }
                                placeholder="Driver name"
                                listening={
                                    listeningField ===
                                    "driverName"
                                }
                                onVoice={() =>
                                    startVoiceInput({
                                        fieldName:
                                            "driverName"
                                    })
                                }
                            />

                        </div>


                        {/* PARTY NO */}

                        <div className="grn-form-group">

                            <label>
                                Party No.
                            </label>

                            <VoiceInput
                                value={
                                    form.partyNo
                                }
                                onChange={
                                    (e) =>
                                        handleChange({
                                            target: {
                                                name:
                                                    "partyNo",
                                                value:
                                                    e.target.value
                                            }
                                        })
                                }
                                placeholder="Party number"
                                listening={
                                    listeningField ===
                                    "partyNo"
                                }
                                onVoice={() =>
                                    startVoiceInput({
                                        fieldName:
                                            "partyNo"
                                    })
                                }
                            />

                        </div>


                        {/* FULL LORRY WEIGHT */}

                        <div className="grn-form-group">

                            <label>
                                Full Lorry Weight (KG)
                            </label>

                            <VoiceInput
                                type="number"
                                value={
                                    form.fullLorryWeightKg
                                }
                                onChange={
                                    (e) =>
                                        handleChange({
                                            target: {
                                                name:
                                                    "fullLorryWeightKg",
                                                value:
                                                    e.target.value
                                            }
                                        })
                                }
                                placeholder="0"
                                listening={
                                    listeningField ===
                                    "fullLorryWeightKg"
                                }
                                onVoice={() =>
                                    startVoiceInput({
                                        fieldName:
                                            "fullLorryWeightKg",
                                        type:
                                            "number"
                                    })
                                }
                            />

                        </div>


                        {/* EMPTY LORRY WEIGHT */}

                        <div className="grn-form-group">

                            <label>
                                Empty Lorry Weight (KG)
                            </label>

                            <VoiceInput
                                type="number"
                                value={
                                    form.emptyLorryWeightKg
                                }
                                onChange={
                                    (e) =>
                                        handleChange({
                                            target: {
                                                name:
                                                    "emptyLorryWeightKg",
                                                value:
                                                    e.target.value
                                            }
                                        })
                                }
                                placeholder="0"
                                listening={
                                    listeningField ===
                                    "emptyLorryWeightKg"
                                }
                                onVoice={() =>
                                    startVoiceInput({
                                        fieldName:
                                            "emptyLorryWeightKg",
                                        type:
                                            "number"
                                    })
                                }
                            />

                        </div>


                        {/* NET WEIGHT - AUTO CALCULATED */}

                        <div className="grn-form-group">

                            <label>
                                Net Weight (KG)
                            </label>

                            <input
                                type="number"
                                value={
                                    Math.max(
                                        0,
                                        Number(form.fullLorryWeightKg || 0) -
                                        Number(form.emptyLorryWeightKg || 0)
                                    )
                                }
                                readOnly
                                className="grn-net-weight"
                            />

                        </div>


                        {/* REMARKS */}

                        <div className="grn-form-group full-width">

                            <label>
                                Remarks
                            </label>

                            <VoiceInput
                                value={
                                    form.remarks
                                }
                                onChange={
                                    (e) =>
                                        handleChange({
                                            target: {
                                                name:
                                                    "remarks",
                                                value:
                                                    e.target.value
                                            }
                                        })
                                }
                                placeholder="Enter remarks"
                                rows={3}
                                listening={
                                    listeningField ===
                                    "remarks"
                                }
                                onVoice={() =>
                                    startVoiceInput({
                                        fieldName:
                                            "remarks"
                                    })
                                }
                            />

                        </div>

                    </div>

                </div>


                {/* =================================================
                    STORAGE DETAILS
                ================================================= */}

                <div className="grn-form-card">

                    <div className="grn-form-card-header">

                        <div>

                            <h2>
                                Storage Details
                            </h2>

                            <p>
                                Chamber, lot and potato storage information
                            </p>

                        </div>


                        <button
                            type="button"
                            className="grn-add-item-button"
                            onClick={
                                addItem
                            }
                        >

                            <Plus
                                size={16}
                            />

                            Add Item

                        </button>

                    </div>


                    {/* CHAMBER LOADING */}

                    {loadingChambers && (

                        <div
                            style={{
                                padding:
                                    "12px 16px",
                                marginBottom:
                                    "15px",
                                borderRadius:
                                    "8px",
                                background:
                                    "#f5f7ff",
                                color:
                                    "#4b5563",
                                fontSize:
                                    "15px"
                            }}
                        >

                            Loading chambers...

                        </div>

                    )}


                    {/* NO CHAMBERS */}

                    {!loadingChambers &&
                        chambers.length === 0 && (

                            <div
                                style={{
                                    padding:
                                        "12px 16px",
                                    marginBottom:
                                        "15px",
                                    borderRadius:
                                        "8px",
                                    background:
                                        "#fff7ed",
                                    color:
                                        "#9a3412",
                                    fontSize:
                                        "15px"
                                }}
                            >

                                No chambers are available.

                                Please check the Chambers table.

                                <button
                                    type="button"
                                    onClick={
                                        loadChambers
                                    }
                                    style={{
                                        marginLeft:
                                            "8px",
                                        border:
                                            "none",
                                        background:
                                            "none",
                                        color:
                                            "#2563eb",
                                        cursor:
                                            "pointer",
                                        fontWeight:
                                            "600"
                                    }}
                                >
                                    Refresh chambers
                                </button>

                            </div>

                        )}


                    {/* ITEMS */}

                    <div className="grn-items-form">

                        {items.map(
                            (
                                item,
                                index
                            ) => {

                                const itemListening =
                                    (field) =>
                                        listeningField ===
                                        `item-${index}-${field}`;


                                return (

                                    <div
                                        className="grn-item-card"
                                        key={index}
                                    >


                                        {/* ITEM NUMBER */}

                                        <div className="grn-item-number">

                                            Item{" "}
                                            {index + 1}

                                        </div>


                                        <div className="grn-item-grid">


                                            {/* CHAMBER */}

                                            <div className="grn-form-group">

                                                <label>
                                                    Chamber *
                                                </label>

                                                <select
                                                    value={
                                                        item.chamberId
                                                    }
                                                    onChange={
                                                        (e) =>
                                                            handleItemChange(
                                                                index,
                                                                "chamberId",
                                                                e.target.value
                                                            )
                                                    }
                                                    required
                                                >

                                                    <option value="">
                                                        Select Chamber
                                                    </option>


                                                    {chambers.map(
                                                        (
                                                            chamber,
                                                            chamberIndex
                                                        ) => {

                                                            const chamberId =
                                                                chamber.id;


                                                            /*
                                                               A / B / C / D
                                                            */

                                                            const chamberName =
                                                                String.fromCharCode(
                                                                    65 +
                                                                    chamberIndex
                                                                );


                                                            return (

                                                                <option
                                                                    key={
                                                                        chamberId
                                                                    }
                                                                    value={
                                                                        chamberId
                                                                    }
                                                                >
                                                                    {
                                                                        chamberName
                                                                    }
                                                                </option>

                                                            );

                                                        }
                                                    )}

                                                </select>

                                            </div>

                                            {/* FLOOR */}

                                            <div className="grn-form-group">
                                                <label>Floor *</label>
                                                <select
                                                    value={item.floorNo ?? ""}
                                                    onChange={(e) =>
                                                        handleItemChange(
                                                            index,
                                                            "floorNo",
                                                            e.target.value
                                                        )
                                                    }
                                                    required
                                                >
                                                    <option value="">Select Floor</option>
                                                    {[1, 2, 3, 4, 5, 6].map((floor) => (
                                                        <option key={floor} value={floor}>
                                                            {floor}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>



                                            {/* LOT NO */}

                                            <div className="grn-form-group">

                                                <label>
                                                    Lot No. *
                                                </label>

                                                <VoiceInput
                                                    value={
                                                        item.lotNo
                                                    }
                                                    onChange={
                                                        (e) =>
                                                            handleItemChange(
                                                                index,
                                                                "lotNo",
                                                                e.target.value
                                                            )
                                                    }
                                                    placeholder="Lot number"
                                                    listening={
                                                        itemListening(
                                                            "lotNo"
                                                        )
                                                    }
                                                    onVoice={() =>
                                                        startVoiceInput({
                                                            itemIndex:
                                                                index,
                                                            itemField:
                                                                "lotNo"
                                                        })
                                                    }
                                                />

                                            </div>


                                            {/* PRODUCT MARK */}

                                            <div className="grn-form-group">

                                                <label>
                                                    Product Mark
                                                </label>

                                                <VoiceInput
                                                    value={
                                                        item.productMark
                                                    }
                                                    onChange={
                                                        (e) =>
                                                            handleItemChange(
                                                                index,
                                                                "productMark",
                                                                e.target.value
                                                            )
                                                    }
                                                    placeholder="Product mark"
                                                    listening={
                                                        itemListening(
                                                            "productMark"
                                                        )
                                                    }
                                                    onVoice={() =>
                                                        startVoiceInput({
                                                            itemIndex:
                                                                index,
                                                            itemField:
                                                                "productMark"
                                                        })
                                                    }
                                                />

                                            </div>


                                            {/* BAGS */}

                                            <div className="grn-form-group">

                                                <label>
                                                    Bags *
                                                </label>

                                                <VoiceInput
                                                    type="number"
                                                    value={
                                                        item.bags
                                                    }
                                                    onChange={
                                                        (e) =>
                                                            handleItemChange(
                                                                index,
                                                                "bags",
                                                                e.target.value
                                                            )
                                                    }
                                                    placeholder="0"
                                                    listening={
                                                        itemListening(
                                                            "bags"
                                                        )
                                                    }
                                                    onVoice={() =>
                                                        startVoiceInput({
                                                            itemIndex:
                                                                index,
                                                            itemField:
                                                                "bags",
                                                            type:
                                                                "number"
                                                        })
                                                    }
                                                />

                                            </div>


                                            {/* ROTTEN BAGS */}

                                            <div className="grn-form-group">

                                                <label>
                                                    Rotten Bags
                                                </label>

                                                <VoiceInput
                                                    type="number"
                                                    value={
                                                        item.rottenBags ?? "0"
                                                    }
                                                    onChange={
                                                        (e) =>
                                                            handleItemChange(
                                                                index,
                                                                "rottenBags",
                                                                e.target.value
                                                            )
                                                    }
                                                    placeholder="0"
                                                    listening={
                                                        itemListening(
                                                            "rottenBags"
                                                        )
                                                    }
                                                    onVoice={() =>
                                                        startVoiceInput({
                                                            itemIndex:
                                                                index,
                                                            itemField:
                                                                "rottenBags",
                                                            type:
                                                                "number"
                                                        })
                                                    }
                                                />

                                            </div>


                                            {/* WEIGHT */}

                                            <div className="grn-form-group">

                                                <label>
                                                    Weight (KG) *
                                                </label>

                                                <VoiceInput
                                                    type="number"
                                                    value={
                                                        item.weightKg
                                                    }
                                                    onChange={
                                                        (e) =>
                                                            handleItemChange(
                                                                index,
                                                                "weightKg",
                                                                e.target.value
                                                            )
                                                    }
                                                    placeholder="0"
                                                    listening={
                                                        itemListening(
                                                            "weightKg"
                                                        )
                                                    }
                                                    onVoice={() =>
                                                        startVoiceInput({
                                                            itemIndex:
                                                                index,
                                                            itemField:
                                                                "weightKg",
                                                            type:
                                                                "number"
                                                        })
                                                    }
                                                />

                                            </div>


                                            {/* LOCATION */}

                                            <div className="grn-form-group">

                                                <label>
                                                    Location
                                                </label>

                                                <VoiceInput
                                                    value={
                                                        item.location
                                                    }
                                                    onChange={
                                                        (e) =>
                                                            handleItemChange(
                                                                index,
                                                                "location",
                                                                e.target.value
                                                            )
                                                    }
                                                    placeholder="Location"
                                                    listening={
                                                        itemListening(
                                                            "location"
                                                        )
                                                    }
                                                    onVoice={() =>
                                                        startVoiceInput({
                                                            itemIndex:
                                                                index,
                                                            itemField:
                                                                "location"
                                                        })
                                                    }
                                                />

                                            </div>


                                            {/* REMOVE */}

                                            {items.length > 1 && (

                                                <button
                                                    type="button"
                                                    className="grn-remove-item"
                                                    onClick={() =>
                                                        removeItem(
                                                            index
                                                        )
                                                    }
                                                    title="Remove item"
                                                >

                                                    <Trash2
                                                        size={17}
                                                    />

                                                    Remove

                                                </button>

                                            )}

                                        </div>

                                    </div>

                                );

                            }
                        )}

                    </div>

                </div>


                {/* =================================================
                    ACTIONS
                ================================================= */}

                <div className="grn-form-actions">


                    <button
                        type="button"
                        className="grn-cancel-button"
                        onClick={
                            handleBack
                        }
                        disabled={
                            loading
                        }
                    >

                        Cancel

                    </button>


                    <button
                        type="submit"
                        className="grn-save-button"
                        disabled={
                            loading ||
                            loadingChambers ||
                            chambers.length === 0
                        }
                    >

                        {loading ? (

                            <>

                                <RefreshCw
                                    size={17}
                                    className="grn-loading-spin"
                                />

                                {
                                    isEditMode
                                        ? "Updating..."
                                        : "Saving..."
                                }

                            </>

                        ) : (

                            <>

                                {isEditMode ? (

                                    <Edit3
                                        size={17}
                                    />

                                ) : (

                                    <Save
                                        size={17}
                                    />

                                )}

                                {
                                    isEditMode
                                        ? "Update Receipt"
                                        : "Save Receipt"
                                }

                            </>

                        )}

                    </button>

                </div>

            </form>

        </div>

    );

};


export default GoodsReceived;